import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import {
    getTicketCategoriesURL,
    createTicketCategoryURL,
    updateTicketCategoryURL,
    deleteTicketCategoryURL
} from '../../../../../utils/urls';

const CategoriesTab = () => {
    const [categories, setCategories] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        priority: 'medium',
        sla_minutes: '',
        color: '#3b82f6',
        is_active: true
    });

    const priorityOptions = [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' },
        { label: 'Urgent', value: 'urgent' },
        { label: 'Critical', value: 'critical' }
    ];

    const statusOptions = [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ];

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setLoading(true);
        try {
            const response = await makeRequest2(getTicketCategoriesURL, 'GET', {});

            if (response.success && response.data && response.data.success) {
                const categoriesData = response.data.data || [];
                setCategories(categoriesData);
                setFilteredCategories(categoriesData);
            } else if (response.success === false || (response.data && response.data.success === false)) {
                const errorData = response.data || response;
                toastify(errorData.error || errorData.message || 'Failed to load categories', 'error');
                setCategories([]);
                setFilteredCategories([]);
            } else {
                toastify('Failed to load categories', 'error');
                setCategories([]);
                setFilteredCategories([]);
            }
        } catch (error) {
            toastify(error.message || 'Error loading categories', 'error');
            setCategories([]);
            setFilteredCategories([]);
        }
        setLoading(false);
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value.toLowerCase();
        setGlobalFilter(value);

        if (!value.trim()) {
            setFilteredCategories(categories);
        } else {
            const filtered = categories.filter(category => {
                return (
                    category.name?.toLowerCase().includes(value) ||
                    category.description?.toLowerCase().includes(value) ||
                    category.priority?.toLowerCase().includes(value)
                );
            });
            setFilteredCategories(filtered);
        }
    };

    const handleAddCategory = () => {
        setIsEdit(false);
        setFormData({
            name: '',
            description: '',
            priority: 'medium',
            sla_minutes: '',
            color: '#3b82f6',
            is_active: true
        });
        setSelectedCategory(null);
        setDialogVisible(true);
    };

    const handleEditCategory = (category) => {
        setIsEdit(true);
        setSelectedCategory(category);
        setFormData({
            name: category.name || '',
            description: category.description || '',
            priority: category.priority || 'medium',
            sla_minutes: category.sla_minutes || '',
            color: category.color || '#3b82f6',
            is_active: category.is_active !== undefined ? category.is_active : true
        });
        setDialogVisible(true);
    };

    const handleSaveCategory = async () => {
        if (!formData.name.trim()) {
            toastify('Category name is required', 'error');
            return;
        }

        if (!formData.priority) {
            toastify('Priority is required', 'error');
            return;
        }

        if (!formData.sla_minutes || formData.sla_minutes <= 0) {
            toastify('SLA minutes must be greater than 0', 'error');
            return;
        }

        try {
            const payload = {
                ...formData,
                sla_minutes: parseInt(formData.sla_minutes)
            };

            let response;
            if (isEdit) {
                const url = updateTicketCategoryURL.replace(':category_id', selectedCategory._id || selectedCategory.id);
                response = await makeRequest2(url, 'PUT', payload);
            } else {
                response = await makeRequest2(createTicketCategoryURL, 'POST', payload);
            }

            if (response.success && response.data && response.data.success) {
                toastify(response.data.message || (isEdit ? 'Category updated successfully' : 'Category created successfully'), 'success');
                setDialogVisible(false);
                loadCategories();
            } else {
                const errorMsg = response.data?.error || response.error || 'Failed to save category';
                toastify(errorMsg, 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to save category', 'error');
        }
    };

    const handleArchiveCategory = (category) => {
        confirmDialog({
            message: `Are you sure you want to archive "${category.name}"? This category will be hidden from active lists but can be restored later.`,
            header: 'Confirm Archive',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-warning',
            accept: () => performArchive(category)
        });
    };

    const performArchive = async (category) => {
        try {
            const url = deleteTicketCategoryURL.replace(':category_id', category._id || category.id);
            const response = await makeRequest2(url, 'DELETE', {});

            if (response.success && response.data && response.data.success) {
                toastify(response.data.message || 'Category archived successfully', 'success');
                loadCategories();
            } else {
                const errorMsg = response.data?.error || response.error || 'Failed to archive category';

                // Show error dialog for active tickets or other errors
                confirmDialog({
                    message: errorMsg,
                    header: 'Cannot Archive Category',
                    icon: 'pi pi-info-circle',
                    acceptLabel: 'OK',
                    rejectClassName: 'hidden',
                    accept: () => {}
                });
            }
        } catch (error) {
            const errorMsg = error.message || 'Failed to archive category';
            confirmDialog({
                message: errorMsg,
                header: 'Error',
                icon: 'pi pi-times-circle',
                acceptLabel: 'OK',
                rejectClassName: 'hidden',
                accept: () => {}
            });
        }
    };

    const priorityBodyTemplate = (rowData) => {
        const severityMap = {
            'low': 'info',
            'medium': 'warning',
            'high': 'warning',
            'urgent': 'danger',
            'critical': 'danger'
        };
        return <Tag value={rowData.priority} severity={severityMap[rowData.priority] || 'info'} />;
    };

    const statusBodyTemplate = (rowData) => {
        const severity = rowData.is_active ? 'success' : 'secondary';
        const value = rowData.is_active ? 'Active' : 'Inactive';
        return <Tag value={value} severity={severity} />;
    };

    const colorBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                    style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: rowData.color,
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                    }}
                />
                <span>{rowData.color}</span>
            </div>
        );
    };

    const slaBodyTemplate = (rowData) => {
        return `${rowData.sla_minutes} minutes`;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => handleEditCategory(rowData)}
                    tooltip="Edit Category"
                />
                <Button
                    icon="pi pi-box"
                    className="p-button-rounded p-button-warning p-button-sm"
                    onClick={() => handleArchiveCategory(rowData)}
                    tooltip="Archive Category"
                />
            </div>
        );
    };

    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={() => setDialogVisible(false)} className="p-button-text" />
            <Button label="Save" icon="pi pi-check" onClick={handleSaveCategory} />
        </div>
    );

    return (
        <>
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    <h5 className="mb-0">Ticket Categories</h5>
                                </div>
                                <div className="col-md-8">
                                    <div className="d-flex gap-2 justify-content-end">
                                        <div className="p-input-icon-left" style={{ minWidth: '300px' }}>
                                            <i className="pi pi-search" />
                                            <InputText
                                                value={globalFilter}
                                                onChange={onGlobalFilterChange}
                                                placeholder="Search categories..."
                                                className="w-100"
                                            />
                                        </div>
                                        <button
                                            className="btn btn-primary"
                                            style={{ color: 'white', whiteSpace: 'nowrap' }}
                                            onClick={handleAddCategory}
                                        >
                                            <i className="ti ti-plus" />
                                            &nbsp;Add New Category
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="dt-responsive table-responsive">
                                <DataTable
                                    value={filteredCategories}
                                    emptyMessage="No categories found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                    loading={loading}
                                >
                                    <Column
                                        field="name"
                                        header="Name"
                                        sortable
                                        style={{ width: '20%' }}
                                    />
                                    <Column
                                        field="description"
                                        header="Description"
                                        style={{ width: '25%' }}
                                    />
                                    <Column
                                        header="Priority"
                                        body={priorityBodyTemplate}
                                        sortable
                                        style={{ width: '12%' }}
                                    />
                                    <Column
                                        header="SLA"
                                        body={slaBodyTemplate}
                                        sortable
                                        style={{ width: '10%' }}
                                    />
                                    <Column
                                        header="Color"
                                        body={colorBodyTemplate}
                                        style={{ width: '15%' }}
                                    />
                                    <Column
                                        header="Status"
                                        body={statusBodyTemplate}
                                        sortable
                                        style={{ width: '10%' }}
                                    />
                                    <Column
                                        header="Actions"
                                        body={actionBodyTemplate}
                                        exportable={false}
                                        style={{ width: '8%' }}
                                    />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                visible={dialogVisible}
                style={{ width: '50vw' }}
                header={isEdit ? 'Edit Category' : 'Add New Category'}
                modal
                footer={dialogFooter}
                onHide={() => setDialogVisible(false)}
            >
                <div className="p-fluid">
                    <div className="field mb-3">
                        <label htmlFor="name">Category Name *</label>
                        <InputText
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="description">Description</label>
                        <InputTextarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="priority">Priority Level *</label>
                        <Dropdown
                            id="priority"
                            value={formData.priority}
                            options={priorityOptions}
                            onChange={(e) => setFormData({ ...formData, priority: e.value })}
                            placeholder="Select priority"
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="sla_minutes">SLA (Minutes) *</label>
                        <InputText
                            id="sla_minutes"
                            type="number"
                            value={formData.sla_minutes}
                            onChange={(e) => setFormData({ ...formData, sla_minutes: e.target.value })}
                            placeholder="e.g. 30"
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="color">Color *</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <InputText
                                id="color"
                                value={formData.color}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                placeholder="#3b82f6"
                            />
                            <input
                                type="color"
                                value={formData.color}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                style={{ width: '50px', height: '38px', cursor: 'pointer' }}
                            />
                        </div>
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="is_active">Status *</label>
                        <Dropdown
                            id="is_active"
                            value={formData.is_active}
                            options={statusOptions}
                            onChange={(e) => setFormData({ ...formData, is_active: e.value })}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default CategoriesTab;
